import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

class DraftNoticeController extends Controller {
  static targets = ['banner']
  static values = {
    scheduleId: Number,
    newUrl: String,
    mechanics: Array,
    textDraft: String,
    textPit: String,
    textResume: String,
    textDismiss: String
  }

  connect () {
    this._checkDraft()

    // Watch the drawer turbo-frame for changes (open/close/submit)
    this._drawerFrame = document.querySelector('turbo-frame#drawer')
    if (this._drawerFrame) {
      this._observer = new MutationObserver(() => this._onDrawerChange())
      this._observer.observe(this._drawerFrame, { childList: true, subtree: true })
    }

    // Also catch successful form submissions that clear the draft
    this._onSubmitEnd = (e) => {
      if (e.detail?.success) setTimeout(() => this._checkDraft(), 200)
    }
    document.addEventListener('turbo:submit-end', this._onSubmitEnd)
  }

  disconnect () {
    if (this._observer) this._observer.disconnect()
    document.removeEventListener('turbo:submit-end', this._onSubmitEnd)
    if (this._debounce) clearTimeout(this._debounce)
  }

  _onDrawerChange () {
    // Debounce — mutations fire many times during frame render
    if (this._debounce) clearTimeout(this._debounce)
    this._debounce = setTimeout(() => this._checkDraft(), 250)
  }

  _checkDraft () {
    const key = `appointment_draft_${this.scheduleIdValue}`
    let data
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) { this._hide(); return }
      data = JSON.parse(raw)
    } catch { this._hide(); return }

    // Draft must have meaningful phone data
    const digits = (data.phone || '').replace(/\D/g, '')
    if (digits.length <= 1) { this._hide(); return }

    // Don't show while drawer is open with the form
    if (this._drawerFrame?.querySelector('dialog[open]')) return

    this._showBanner(data)
  }

  _showBanner (data) {
    const mechanic = this.mechanicsValue.find(m => String(m.id) === String(data.mechanicId))
    const mechanicName = mechanic ? mechanic.name : ''
    const pit = mechanic?.box ? this.textPitValue.replace('%{n}', mechanic.box) : ''
    const time = this._formatTimeRange(data.startsAt, data.endsAt)

    // Build info chips
    const chips = [mechanicName, pit, time].filter(Boolean)

    const banner = this.bannerTarget
    banner.className = 'draft-notice'
    banner.innerHTML = `
      <div class="draft-notice__body">
        <svg class="draft-notice__icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
        <span class="draft-notice__text">${this._escape(this.textDraftValue)}</span>
        ${chips.map(c => `<span class="draft-notice__chip">${this._escape(c)}</span>`).join('')}
      </div>
      <div class="draft-notice__actions">
        <button type="button" class="draft-notice__resume" data-action="click->draft-notice#resume">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="size-4">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
          ${this._escape(this.textResumeValue)}
        </button>
        <button type="button" class="draft-notice__dismiss" data-action="click->draft-notice#dismiss">
          ${this._escape(this.textDismissValue)}
        </button>
      </div>
    `
    banner.classList.remove('hidden')
  }

  _hide () {
    if (this.hasBannerTarget) {
      this.bannerTarget.className = 'hidden'
      this.bannerTarget.innerHTML = ''
    }
  }

  resume () {
    this._hide()
    const drawerFrame = document.querySelector('turbo-frame#drawer')
    if (drawerFrame) {
      drawerFrame.src = this.newUrlValue
    }
  }

  dismiss () {
    const key = `appointment_draft_${this.scheduleIdValue}`
    try { sessionStorage.removeItem(key) } catch {}
    this._hide()
  }

  // "10:00:00.000" or "10:00:00" or "10:00" → "10:00"
  _formatTime (raw) {
    if (!raw) return ''
    return raw.replace(/^(\d{1,2}:\d{2}).*/, '$1')
  }

  _formatTimeRange (start, end) {
    const s = this._formatTime(start)
    const e = this._formatTime(end)
    if (s && e) return `${s} – ${e}`
    if (s) return s
    return ''
  }

  _escape (str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}

stimulus.register('draft-notice', DraftNoticeController)
